import { ProperNounClient, DisplayOrderClient } from "@/lib/clients";
import {
  ProperNoun,
  ProperNounCustomCategory,
  CreateProperNoun,
  UpdateProperNoun,
  DeleteProperNoun,
  CreateProperNounCustomCategory,
  UpdateProperNounCustomCategory,
  DeleteProperNounCustomCategory,
} from "@/lib/models";
import { Kind } from "@/lib/models/sortOrder";
import { ActionContext, ActionTree, GetterTree, MutationTree, Store } from "vuex";

const properNounClient = new ProperNounClient();
const displayOrderClient = new DisplayOrderClient();

/**
 * displayOrder の order 配列に従ってカスタムカテゴリを並べ替える（order に無いものは末尾）。
 * order にはカテゴリIDそのもの（properNounCustomCategoryId）が入る。
 */
function sortByDisplayOrder(
  categories: ProperNounCustomCategory[],
  order: string[]
): ProperNounCustomCategory[] {
  const indexOfCategory = (category: ProperNounCustomCategory) => order.indexOf(category.properNounCustomCategoryId);
  return [...categories].sort((a, b) => {
    const ia = indexOfCategory(a);
    const ib = indexOfCategory(b);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

// 一括置換イベント（開いているエディタへ未保存編集を保ったまま反映するために通知する）
export type BulkReplaceEvent = { oldName: string; newName: string };

export interface ProperNounState {
  properNounList: ProperNoun[];
  customCategoryList: ProperNounCustomCategory[];
  // タブ（登場人物/世界観/資料/固有名詞カテゴリ/カスタム）の表示順。各要素の生ID配列
  // （character / worldview / 資料属性ID / properNounCategory値 / カスタムカテゴリID）
  categoryOrder: string[];
  // 直近の一括置換イベント（開いているエディタへ未保存編集を保ったまま反映するために通知する）
  bulkReplaceEvent: BulkReplaceEvent | null;
}

type Getters = {
  properNounList(state: ProperNounState): ProperNoun[];
  customCategoryList(state: ProperNounState): ProperNounCustomCategory[];
  categoryOrder(state: ProperNounState): string[];
  bulkReplaceEvent(state: ProperNounState): BulkReplaceEvent | null;
  properNoun(state: ProperNounState): (properNounId: string) => ProperNoun | undefined;
  properNounsByCategory(state: ProperNounState): (category: string) => ProperNoun[];
  customCategory(state: ProperNounState): (properNounCustomCategoryId: string) => ProperNounCustomCategory | undefined;
};

type Actions = {
  initialize: (
    this: Store<{}>,
    injectee: ActionContext<ProperNounState, {}>,
    payload: string
  ) => Promise<void>;
  createProperNoun: (
    this: Store<{}>,
    injectee: ActionContext<ProperNounState, {}>,
    payload: CreateProperNoun
  ) => Promise<ProperNoun>;
  updateProperNoun: (
    this: Store<{}>,
    injectee: ActionContext<ProperNounState, {}>,
    payload: UpdateProperNoun
  ) => Promise<void>;
  deleteProperNoun: (
    this: Store<{}>,
    injectee: ActionContext<ProperNounState, {}>,
    payload: DeleteProperNoun
  ) => Promise<void>;
  createCustomCategory: (
    this: Store<{}>,
    injectee: ActionContext<ProperNounState, {}>,
    payload: CreateProperNounCustomCategory
  ) => Promise<ProperNounCustomCategory>;
  updateCustomCategory: (
    this: Store<{}>,
    injectee: ActionContext<ProperNounState, {}>,
    payload: UpdateProperNounCustomCategory
  ) => Promise<void>;
  deleteCustomCategory: (
    this: Store<{}>,
    injectee: ActionContext<ProperNounState, {}>,
    payload: DeleteProperNounCustomCategory
  ) => Promise<void>;
  reorderCategories: (
    this: Store<{}>,
    injectee: ActionContext<ProperNounState, {}>,
    payload: { novelId: string; order: string[] }
  ) => Promise<void>;
};

const mutations: MutationTree<ProperNounState> = {
  setProperNounList(state, payload: ProperNoun[]) {
    state.properNounList = payload;
  },
  pushProperNoun(state, payload: ProperNoun) {
    state.properNounList.push(payload);
  },
  updateProperNounInState(state, payload: ProperNoun) {
    state.properNounList = state.properNounList.map((item) =>
      item.properNounId === payload.properNounId ? payload : item
    );
  },
  removeProperNoun(state, payload: ProperNoun) {
    state.properNounList = state.properNounList.filter(
      (item) => item.properNounId !== payload.properNounId
    );
  },
  setCustomCategoryList(state, payload: ProperNounCustomCategory[]) {
    state.customCategoryList = payload;
  },
  setCategoryOrder(state, payload: string[]) {
    state.categoryOrder = payload;
  },
  setBulkReplaceEvent(state, payload: BulkReplaceEvent | null) {
    state.bulkReplaceEvent = payload;
  },
  pushCustomCategory(state, payload: ProperNounCustomCategory) {
    state.customCategoryList.push(payload);
  },
  updateCustomCategoryInState(state, payload: ProperNounCustomCategory) {
    state.customCategoryList = state.customCategoryList.map((item) =>
      item.properNounCustomCategoryId === payload.properNounCustomCategoryId ? payload : item
    );
  },
  removeCustomCategory(state, payload: ProperNounCustomCategory) {
    state.customCategoryList = state.customCategoryList.filter(
      (item) => item.properNounCustomCategoryId !== payload.properNounCustomCategoryId
    );
  },
};

const actions: ActionTree<ProperNounState, {}> & Actions = {
  async initialize({ commit }, novelId) {
    const [properNounList, customCategoryList, displayOrder] = await Promise.all([
      properNounClient.fetchAllProperNouns(novelId),
      properNounClient.fetchAllCustomCategories(novelId),
      displayOrderClient.fetchDisplayOrder({ novelId, kind: Kind.PROPER_NOUN_CATEGORY }),
    ]);
    commit("setProperNounList", properNounList);
    commit("setCategoryOrder", displayOrder.order);
    commit("setCustomCategoryList", sortByDisplayOrder(customCategoryList, displayOrder.order));
  },
  async createProperNoun({ commit }, payload) {
    const properNoun = await properNounClient.createProperNoun(payload);
    commit("pushProperNoun", properNoun);
    return properNoun;
  },
  async updateProperNoun({ commit }, payload) {
    const properNoun = await properNounClient.updateProperNoun(payload);
    commit("updateProperNounInState", properNoun);
  },
  async deleteProperNoun({ commit }, payload) {
    const properNoun = await properNounClient.deleteProperNoun(payload);
    commit("removeProperNoun", properNoun);
  },
  async createCustomCategory({ commit }, payload) {
    const category = await properNounClient.createCustomCategory(payload);
    commit("pushCustomCategory", category);
    return category;
  },
  async updateCustomCategory({ commit }, payload) {
    const category = await properNounClient.updateCustomCategory(payload);
    commit("updateCustomCategoryInState", category);
  },
  async deleteCustomCategory({ commit }, payload) {
    const category = await properNounClient.deleteCustomCategory(payload);
    commit("removeCustomCategory", category);
  },
  async reorderCategories({ commit, state }, { novelId, order }) {
    // 先にローカル順序を楽観的に反映し、続けて永続化する
    const previous = state.categoryOrder;
    commit("setCategoryOrder", order);
    // カスタムカテゴリ一覧（カテゴリ選択や編集一覧の表示順）も新しい順序に合わせる
    commit("setCustomCategoryList", sortByDisplayOrder(state.customCategoryList, order));
    try {
      await displayOrderClient.updateDisplayOrder({
        novelId,
        kind: Kind.PROPER_NOUN_CATEGORY,
        order,
      });
    } catch (error) {
      // 永続化に失敗したら元の順序へロールバックする
      commit("setCategoryOrder", previous);
      commit("setCustomCategoryList", sortByDisplayOrder(state.customCategoryList, previous));
      throw error;
    }
  },
};

const getters: GetterTree<ProperNounState, {}> & Getters = {
  properNounList: (state) => state.properNounList,
  customCategoryList: (state) => state.customCategoryList,
  categoryOrder: (state) => state.categoryOrder,
  bulkReplaceEvent: (state) => state.bulkReplaceEvent,
  properNoun: (state) => (properNounId: string) =>
    state.properNounList.find((x) => x.properNounId === properNounId),
  properNounsByCategory: (state) => (category: string) =>
    state.properNounList.filter((x) => x.properNounCategory === category),
  customCategory: (state) => (properNounCustomCategoryId: string) =>
    state.customCategoryList.find((x) => x.properNounCustomCategoryId === properNounCustomCategoryId),
};

export default {
  namespaced: true,
  state: (): ProperNounState => ({
    properNounList: [],
    customCategoryList: [],
    categoryOrder: [],
    bulkReplaceEvent: null,
  }),
  getters,
  actions,
  mutations,
};
